import { useCallback, useEffect, useRef, useState } from 'react';
import { GalleryPhoto, mediaUrl } from '../../../lib/gallery/api';

export interface ILightboxProps {
    photos: GalleryPhoto[];
    index: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
}

const SWIPE_MIN_PX = 50;

const LightboxImage = ({ photo }: { photo: GalleryPhoto }) => {
    const [loaded, setLoaded] = useState(false);
    return (
        <img
            className={`galeria-lightbox__image ${loaded ? 'galeria-lightbox__image--loaded' : ''}`}
            src={mediaUrl(photo.path, 'webp', photo.m)}
            alt={photo.name}
            onLoad={() => setLoaded(true)}
        />
    );
};

export const Lightbox = ({ photos, index, onClose, onNavigate }: ILightboxProps) => {
    const photo = photos[index];
    const hasPrev = index > 0;
    const hasNext = index < photos.length - 1;
    const closeRef = useRef<HTMLButtonElement>(null);
    const touchStartX = useRef<number | null>(null);

    const goPrev = useCallback(() => { if (hasPrev) onNavigate(index - 1); }, [hasPrev, index, onNavigate]);
    const goNext = useCallback(() => { if (hasNext) onNavigate(index + 1); }, [hasNext, index, onNavigate]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, goPrev, goNext]);

    // blokada przewijania strony + fokus na przycisku zamknięcia (i powrót fokusu po zamknięciu)
    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus();
        };
    }, []);

    // wczytaj z wyprzedzeniem sąsiednie zdjęcia
    useEffect(() => {
        [index - 1, index + 1].forEach((i) => {
            const neighbour = photos[i];
            if (neighbour) new Image().src = mediaUrl(neighbour.path, 'webp', neighbour.m);
        });
    }, [index, photos]);

    if (!photo) return null;

    return (
        <div
            className="galeria-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Podgląd zdjęcia ${photo.name}`}
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
                if (touchStartX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                touchStartX.current = null;
                if (dx > SWIPE_MIN_PX) goPrev();
                else if (dx < -SWIPE_MIN_PX) goNext();
            }}
        >
            <div className="galeria-lightbox__backdrop" onClick={onClose} />

            <div className="galeria-lightbox__stage">
                <LightboxImage key={photo.path} photo={photo} />
            </div>

            <button ref={closeRef} type="button" className="galeria-lightbox__btn galeria-lightbox__close" onClick={onClose} aria-label="Zamknij">×</button>
            {hasPrev && <button type="button" className="galeria-lightbox__btn galeria-lightbox__prev" onClick={goPrev} aria-label="Poprzednie zdjęcie">‹</button>}
            {hasNext && <button type="button" className="galeria-lightbox__btn galeria-lightbox__next" onClick={goNext} aria-label="Następne zdjęcie">›</button>}

            <div className="galeria-lightbox__bar">
                <span className="galeria-lightbox__name">{photo.name}</span>
                <span className="galeria-lightbox__right">
                    <a className="galeria-lightbox__download" href={mediaUrl(photo.path, 'orig', photo.m)} download={photo.name}>Pobierz oryginał</a>
                    <span>{index + 1} / {photos.length}</span>
                </span>
            </div>
        </div>
    );
};
